import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { FileAudio, FileVideo, Trash } from "lucide-react";
import { Theme } from "../../../theme";

export type MediaType = "video" | "audio";

/// URL du fichier déjà enregistré, fichier fraîchement sélectionné, ou rien.
export type MediaPickerValue = string | File | null;

export interface MediaPickerProps {
  mediaType: MediaType;
  readOnly?: boolean;
  value: MediaPickerValue;
  onSelect: (value: File | null) => void;
}

const MEGABYTE = 1024 * 1024;

/// Formats et tailles acceptés : à garder alignés sur les champs `videoFile` / `audioFile` de
/// `topicParts` (migration `1784600000_topicParts_media_files.js`), sans quoi PocketBase refuse
/// le fichier après l'envoi.
const MEDIA_RULES: Record<MediaType, { mimeTypes: string[]; maxSize: number }> = {
  video: {
    mimeTypes: ["video/mp4", "video/quicktime", "video/webm", "video/x-m4v"],
    maxSize: 500 * MEGABYTE,
  },
  audio: {
    mimeTypes: [
      "audio/mpeg",
      "audio/mp4",
      "audio/x-m4a",
      "audio/aac",
      "audio/wav",
      "audio/x-wav",
      "audio/ogg",
    ],
    maxSize: 100 * MEGABYTE,
  },
};

/// Nom de fichier lisible à partir d'une URL PocketBase (`.../<nom>_<suffixe>.<ext>`).
function fileNameFromUrl(url: string): string {
  try {
    const name = decodeURIComponent(new URL(url).pathname.split("/").pop() ?? "");
    return name || url;
  } catch {
    return url;
  }
}

function formatSize(bytes: number): string {
  return `${(bytes / MEGABYTE).toFixed(1)} Mo`;
}

/// Sélecteur d'un fichier vidéo ou audio, avec lecteur d'aperçu.
///
/// La valeur reste une URL tant que l'utilisateur ne change rien : le repository n'envoie alors
/// pas le champ, et le fichier existant est conservé. `null` demande la suppression du fichier.
const MediaPicker: React.FC<MediaPickerProps> = ({
  mediaType,
  readOnly,
  value,
  onSelect,
}) => {
  const { t } = useTranslation();
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const rules = MEDIA_RULES[mediaType];

  useEffect(() => {
    if (value instanceof File) {
      const url = URL.createObjectURL(value);
      setBlobUrl(url);
      return () => URL.revokeObjectURL(url);
    }
    setBlobUrl(null);
  }, [value]);

  const playerUrl = value instanceof File ? blobUrl : value || null;

  const displayName = useMemo(() => {
    if (value instanceof File) return `${value.name} (${formatSize(value.size)})`;
    if (typeof value === "string" && value) return fileNameFromUrl(value);
    return null;
  }, [value]);

  const handleFileChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      e.target.value = "";
      if (!file) return;

      if (!rules.mimeTypes.includes(file.type)) {
        setError(`media.validation.badFormat.${mediaType}`);
        return;
      }
      if (file.size > rules.maxSize) {
        setError(`media.validation.maxFileSize.${mediaType}`);
        return;
      }

      setError(null);
      onSelect(file);
    },
    [mediaType, onSelect, rules],
  );

  const handleClear = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      e.stopPropagation();
      setError(null);
      onSelect(null);
    },
    [onSelect],
  );

  const Icon = mediaType === "video" ? FileVideo : FileAudio;

  const picker = (
    <div className="flex items-center w-full">
      <Icon size={20} className="shrink-0 text-gray-500 dark:text-gray-400" />
      <p
        className={`ms-3 flex-1 min-w-0 text-left truncate ${
          displayName
            ? "text-gray-900 dark:text-gray-100"
            : "text-gray-500 dark:text-gray-600"
        }`}
      >
        {displayName ??
          (readOnly
            ? t(`media.noFile.${mediaType}`)
            : t(`media.select.${mediaType}`))}
      </p>
      {!readOnly && value && (
        <button
          type="button"
          className="flex shrink-0 m-1 p-1 text-red-500 hover:text-red-700"
          title={t("media.remove")}
          onClick={handleClear}
        >
          <Trash size={16} />
        </button>
      )}
    </div>
  );

  return (
    <div className="space-y-2">
      {readOnly ? (
        <div className={`p-2 ${Theme.input}`}>{picker}</div>
      ) : (
        <div
          role="button"
          tabIndex={0}
          className={`p-2 flex items-center select-none cursor-pointer ${Theme.input}`}
          onClick={() => fileInputRef.current?.click()}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              fileInputRef.current?.click();
            }
          }}
        >
          <input
            ref={fileInputRef}
            type="file"
            className="hidden"
            accept={rules.mimeTypes.join(",")}
            onChange={handleFileChange}
          />
          {picker}
        </div>
      )}
      {playerUrl &&
        (mediaType === "video" ? (
          <video
            key={playerUrl}
            src={playerUrl}
            controls
            preload="metadata"
            className="w-full max-h-[360px] rounded bg-black"
          />
        ) : (
          <audio
            key={playerUrl}
            src={playerUrl}
            controls
            preload="metadata"
            className="w-full"
          />
        ))}
      {error && (
        <p className="text-sm text-red-600 dark:text-red-400">{t(error)}</p>
      )}
    </div>
  );
};

export default MediaPicker;
